import {
  ActionIcon,
  Avatar,
  Button,
  Group,
  Paper,
  rem,
  Text,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import {
  IconFile,
  IconFileSpreadsheet,
  IconFileText,
  IconTrash,
} from "@tabler/icons-react";
import {
  convertToBase64,
  getFileSize,
} from "@components/Tax Pro Only/Modals/EmailModal/utils.ts";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";
import type { EmailAttachment } from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import { notifications } from "@mantine/notifications";

export const EmailAttachments: React.FC = () => {
  const { attachments, setAttachments } = useEmailStore();

  const handleFilesChange = async (files: File[]) => {
    if (files.length + attachments.length > 5) {
      notifications.show({
        message: "You can only add up to 5 files.",
        color: "blue",
      });
      return;
    }

    const newAttachments = await Promise.all(
      files.map(async (file) => {
        const base64 = await convertToBase64(file);
        return {
          filename: file.name,
          content: base64,
          mimetype: file.type,
        };
      })
    );

    const updatedAttachments = [...attachments, ...newAttachments];
    setAttachments(updatedAttachments);
  };

  const removeFile = (index: number) => {
    const updatedFiles = attachments.filter((_, i) => i !== index);
    setAttachments(updatedFiles);
  };

  const getFileDisplay = (file: EmailAttachment) => {
    if (file.mimetype.startsWith("image/")) {
      return (
        <img
          src={`data:${file.mimetype};base64,${file.content}`}
          alt={file.filename}
          width={30}
          height={30}
        />
      );
    } else if (
      file.mimetype.includes("spreadsheet") ||
      file.filename.endsWith(".csv")
    ) {
      return <IconFileSpreadsheet size={rem(16)} />;
    } else if (file.mimetype.includes("pdf")) {
      return <IconFileText size={rem(16)} />;
    }
    return <IconFile size={rem(16)} />;
  };

  return (
    <div>
      <Text size="sm">Attachments</Text>
      <Text size="xs" color="dimmed" mb="xs">
        All recipients will receive attachments—up to 5 files, each no larger
        than 5MB. Accepted file types: PDF, JPG, PNG, GIF, HEIF, HEIC, XLSX,
        CSV.
      </Text>

      {/* File List */}
      {attachments.length > 0 && (
        <Paper shadow="xs" radius="md" p="sm" mb="md">
          {attachments.map((file, index) => (
            <Group key={file.filename} align="center">
              <Group>
                <Avatar size={30} radius="xs">
                  {getFileDisplay(file)}
                </Avatar>
                <Text size="sm">
                  {file.filename} ({getFileSize(file.content)}MB)
                </Text>
              </Group>
              <ActionIcon
                color="red"
                variant="light"
                onClick={() => removeFile(index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
        </Paper>
      )}

      {/* File Upload Button */}
      <Dropzone
        onDrop={handleFilesChange}
        multiple
        accept={[
          "image/*",
          "application/pdf",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/csv",
        ]}
        maxFiles={5}
        maxSize={5 * 1024 ** 2} // 5MB
        activateOnClick
      >
        <Button fullWidth variant="default">
          Attach file
        </Button>
      </Dropzone>
    </div>
  );
};
